import { rem } from 'polished'
import { useCallback } from 'react'
import { Controller, useController, useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import AddressDisplay from './Fields/AddressDisplay'
import {
  priceFieldProps,
  priceRegisterConfig,
  useFieldValidation,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import RHFDatePickerInput from './Fields/RHFDatePickerInput'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import ShowMore from './Fields/ShowMore'
import TimeInput from './Fields/TimeInput'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import useEndPlaceIsSame from './Hooks/useEndPlaceIsSame'
import useEnsureGeoPlace from './Hooks/useEnsureGeoPlace'
import type { CreateCarRentalItemViewModel } from '../Create/models'
import type { PatchCarRentalItemViewModel } from '../Edit/models'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import type {
  TripAddressAutocompleteOption,
} from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import TripAddressAutocomplete from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import type { AutocompletePlaceParam } from 'tripPlanner/types/google'
import {
  ADDRESS_RESPONSE_DETAILS,
} from 'tripPlanner/types/google'
import { convertGoogleAddressComponentsToRegion } from 'tripPlanner/utils'

const StyledVerticalSpacer = styled(VerticalSpacer)`
  margin-top: ${rem(20)};
  margin-bottom: ${rem(20)};
`

const EndPlaceContainer = styled.div`
  padding-bottom: ${rem(8)};
`

const DividerContainer = styled.div`
  height: ${rem(24)};
`

const PriceInput = styled(Input)`
  width: ${rem(100)};
`

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = []

const START_GEO_DEP_KEYS: Array<string> = ['startPlace.name']
const END_GEO_DEP_KEYS: Array<string> = ['endPlace.name']

interface Props {
  tripId: string
}

function CarFields({ tripId }: Props) {
  const { required, urlRegisterConfig } = useFieldValidation()
  const intl = useIntl()
  const methods = useFormContext<
    CreateCarRentalItemViewModel | PatchCarRentalItemViewModel
  >()
  const { watch, control, setValue, register, clearErrors } = methods

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  useEnsureGeoPlace(methods, 'startPlace', START_GEO_DEP_KEYS)
  useEnsureGeoPlace(methods, 'endPlace', END_GEO_DEP_KEYS)

  const startPlace = watch('startPlace')
  const endPlace = watch('endPlace')

  useEndPlaceIsSame(methods)

  const handleAddressChange = useCallback(
    (
      placeName: 'startPlace' | 'endPlace',
      selectedOption: TripAddressAutocompleteOption,
    ) => {
      setValue(
        `${placeName}.name`,
        selectedOption.details?.name || selectedOption.label,
      )

      setValue(
        `${placeName}.type`,
        selectedOption.prediction ? 'GEO' : 'CUSTOM',
      )

      if (selectedOption.prediction) {
        const { place_id } = selectedOption.prediction
        setValue(`${placeName}.id`, place_id)
      }

      if (selectedOption.details) {
        const { geometry, formatted_address, website, address_components } =
          selectedOption.details

        if (address_components) {
          const region =
            convertGoogleAddressComponentsToRegion(address_components)
          if (region) {
            setValue(`${placeName}.region`, region)
          }
        }

        if (website) {
          setValue('url', website)
        }

        formatted_address && setValue(`${placeName}.address`, formatted_address)

        if (geometry?.location) {
          setValue(`${placeName}.point.lat`, geometry.location.lat)
          setValue(`${placeName}.point.lng`, geometry.location.lng)
        }
      }
    },
    [setValue],
  )

  const handleStartAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('startPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const handleEndAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('endPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const {
    fieldState: { error: endPlaceError },
  } = useController({ name: 'endPlace' })

  const handleSetStartPlaceToEndPlace = useCallback(() => {
    setValue('endPlace', startPlace)

    // React hook form not recognizing valid field after this update
    if (endPlaceError) {
      clearErrors('endPlace')
    }
  }, [setValue, startPlace, clearErrors, endPlaceError])

  const pricePlaceholder = intl.formatNumber(0)

  const websitePlaceholder = intl.formatMessage({
    id: 'YJDWIzhAMQIYT78QXghI',
    defaultMessage: 'https://',
    description: 'Input placeholder text for website URL input field',
  })

  const additionalNotesLabel = intl.formatMessage({
    id: 'ubTjyMVkR4ja2sYXdeCk',
    defaultMessage: 'Additional notes',
    description: 'Form label for additional notes field in car rental trip item editor',
  })

  const notesPlaceholder = intl.formatMessage({
    id: 'ZLW6CALtblNj7rhRmjXd',
    defaultMessage: 'Add notes about this car rental',
    description: 'Placeholder text for notes textarea in car rental trip item editor',
  })

  const copyAddressHelpText = <BodyText variant="medium">
    <FormattedMessage
      id="zQU__CIyCQeUb5ayD3XB"
      defaultMessage="Add same address as <link>pick up location</link>"
      description="Link to copy pick up address to drop off address in car rental form"
      values={{
        link: (chunks) => <TextLink onClick={handleSetStartPlaceToEndPlace} underline={false}>{chunks}</TextLink>,
      }}
    />
  </BodyText>

  return (
    <StyledVerticalSpacer gap={16}>
      <Input
        {...register('companyName')}
        label={intl.formatMessage({
          id: 'Mkxvm6z5BxGWKQ96Vayk',
          defaultMessage: 'Rental company',
          description: 'Form label for rental company name field in car rental trip item editor',
        })}
        placeholder={intl.formatMessage({
          id: '6-Ieo2Ppw21Dq6O4lS4l',
          defaultMessage: 'Enter a rental company',
          description: 'Placeholder text for rental company input in car rental trip item editor',
        })}
      />

      <Heading variant="heading4">{intl.formatMessage({
        id: 'CnjlSf7TU4VE2qsxyawh',
        defaultMessage: 'Pick up details',
        description: 'Section heading for pick up details in car rental trip item editor',
      })}
      </Heading>

      <TripAddressAutocomplete
        {...register('startPlace.name', { required })}
        required
        label={intl.formatMessage({
          id: 'It6gXyCQdabpGZMsaO8d',
          defaultMessage: 'Pick up location',
          description: 'Form label for pick up location field in car rental trip item editor',
        })}
        placeholder={intl.formatMessage({
          id: 'C2pRxS0yAl39COUsZp2V',
          defaultMessage: 'Search for location',
          description: 'Placeholder text for location search input in car rental trip item editor',
        })}
        initPlaceId={startPlace?.type === 'GEO' ? startPlace.id : undefined}
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        details={ADDRESS_RESPONSE_DETAILS}
        onPlaceSelect={handleStartAddressChange}
        startIcon={<LineMapMarkerIcon />}
      />
      {startPlace?.address && (
        <AddressDisplay
          title={intl.formatMessage({
            id: 'FWjAhrGxyI3tmtgTXJe_',
            defaultMessage: 'Address',
            description: 'Form field label for address display in car rental trip item',
          })}
          value={startPlace.address}
        />
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="startDate"
          label={intl.formatMessage({
            id: '-bfRfX99UsC3H5t7yD5U',
            defaultMessage: 'Pick up date',
            description: 'Form label for pick up date field in car rental trip item editor',
          })}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('startTime')}
          label={intl.formatMessage({
            id: 'C93Y88M3uo9D1faAA001',
            defaultMessage: 'Time',
            description: 'Form label for time field in car rental trip item editor',
          })}
          placeholder={intl.formatMessage({
            id: 'kRzyMrGTUvEWejjQ4W-E',
            defaultMessage: 'Select time',
            description: 'Placeholder text for time picker in car rental trip item editor',
          })}
        />
      </Group>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Heading variant="heading4">{intl.formatMessage({
        id: 'dW2HEdRoP2nifT_QYAQ-',
        defaultMessage: 'Drop off details',
        description: 'Section heading for drop off details in car rental trip item editor',
      })}
      </Heading>

      <EndPlaceContainer>
        <TripAddressAutocomplete
          {...register('endPlace.name', { required })}
          required
          label={intl.formatMessage({
            id: '0GdwJ7olHTLxS8DlAU-v',
            defaultMessage: 'Drop-off location',
            description: 'Form label for drop-off location field in car rental trip item editor',
          })}
          placeholder={intl.formatMessage({
            id: 'C2pRxS0yAl39COUsZp2V',
            defaultMessage: 'Search for location',
            description: 'Placeholder text for location search input in car rental trip item editor',
          })}
          initPlaceId={endPlace?.type === 'GEO' ? endPlace.id : undefined}
          autocompleteTypes={AUTOCOMPLETE_TYPES}
          details={ADDRESS_RESPONSE_DETAILS}
          onPlaceSelect={handleEndAddressChange}
          startIcon={<LineMapMarkerIcon />}
          helpText={copyAddressHelpText}
        />
        {endPlaceError && copyAddressHelpText}
      </EndPlaceContainer>

      {endPlace?.address && (
        <AddressDisplay
          title={intl.formatMessage({
            id: 'FWjAhrGxyI3tmtgTXJe_',
            defaultMessage: 'Address',
            description: 'Form field label for address display in car rental trip item',
          })}
          value={endPlace.address}
        />
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="endDate"
          label={intl.formatMessage({
            id: 'w5TXhLLuO8HOi1FeKwio',
            defaultMessage: 'Drop-off date',
            description: 'Form label for drop-off date field in car rental trip item editor',
          })}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('endTime')}
          label={intl.formatMessage({
            id: 'C93Y88M3uo9D1faAA001',
            defaultMessage: 'Time',
            description: 'Form label for time field in car rental trip item editor',
          })}
          placeholder={intl.formatMessage({
            id: 'kRzyMrGTUvEWejjQ4W-E',
            defaultMessage: 'Select time',
            description: 'Placeholder text for time picker in car rental trip item editor',
          })}
        />
      </Group>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      <ShowMore>
        <PriceInput
          {...register('price', { ...priceRegisterConfig })}
          label={intl.formatMessage({
            id: '0HgaFAK41n26ZzSTphe-',
            defaultMessage: 'Price',
            description: 'Form label for price field in car rental trip item editor',
          })}
          placeholder={pricePlaceholder}
          {...priceFieldProps}
        />

        <Input
          {...register('url', { ...urlRegisterConfig })}
          label={intl.formatMessage({
            id: 'jWpmw_0IVxkmqd469q8i',
            defaultMessage: 'Website',
            description: 'Form label for website URL field in car rental trip item editor',
          })}
          placeholder={websitePlaceholder}
        />

        <TextArea
          {...register('notes')}
          placeholder={notesPlaceholder}
          label={additionalNotesLabel}
          resize="vertical"
          size="tall"
        />
      </ShowMore>
    </StyledVerticalSpacer>
  )
}

export default CarFields
